// @flow
// $FlowFixMe
import React, { useMemo, type Node, Fragment } from "react";
import { get, upperFirst, some } from "lodash";
import type { RaceCardRunner, RaceTypeCodeEnum } from "@tvg/types/Race";
import useLocalStorage from "@tvg/utils/useLocalStorage";
import type { HandicappingRunnerFlags } from "../RunnerFlags";
import buildColor from "../../_static/ColorPalette";
import Icon from "../../_static/Icons";
import { changes } from "../../_static/Icons/icons";
import RunnerFlags from "../RunnerFlags";
import {
  RunnerInfoContainer,
  RunnerInfoHeader,
  RunnerInfoTitle,
  RunnerInfoShowMoreLink,
  RunnerMainInfo,
  RunnerFlagsContainer,
  RunnerComplementaryInfo,
  ComplementaryInfo,
  ComplementaryInfoLabel,
  ComplementaryInfoValue,
  IconContainer
} from "./styled-components";

type Props = {
  title: string,
  runner: RaceCardRunner,
  raceTypeCode: RaceTypeCodeEnum,
  children: Node,
  qaLabel: string,
  handicappingRunnerFlags: HandicappingRunnerFlags
};

const RUNNER_INFO_SHOW_EXPANDED_KEY = "ppRunnerInfoExpanded";

const RunnerInfo = (props: Props) => {
  const [showMoreInfo, setShowMoreInfo] = useLocalStorage(
    RUNNER_INFO_SHOW_EXPANDED_KEY,
    true
  );
  const mainInfo = ["age", "sex", "med", "weight"];
  const complementaryInfo = [
    "jockey",
    "trainer",
    "ownerName",
    "sire",
    "dam",
    "damSire"
  ];
  const complementaryInfoKeyChanges =
    props.runner && props.runner.hasJockeyChanges ? ["jockey"] : [];

  const renderRunnerFlags = () => {
    const flags = get(props, "runner.timeform.flags", {});
    const hasFlags = some(flags, (flag) => !!flag);
    return (
      hasFlags && (
        <RunnerFlagsContainer>
          <RunnerFlags
            runnerId={props.runner.runnerId}
            runnerName={props.runner.horseName}
            raceTypeCode={props.raceTypeCode}
            flags={flags}
            handicappingRunnerFlags={props.handicappingRunnerFlags}
            helpShown
            hasBackNavigation
          />
        </RunnerFlagsContainer>
      )
    );
  };

  return useMemo(
    () =>
      props.runner && (
        <RunnerInfoContainer data-qa-label={props.qaLabel}>
          <RunnerInfoHeader>
            <RunnerInfoTitle>{props.title}</RunnerInfoTitle>
            <RunnerInfoShowMoreLink
              onClick={() => setShowMoreInfo(!showMoreInfo)}
              data-qa-label={`${props.qaLabel}-show-more`}
            >
              {`${showMoreInfo ? "Hide" : "Show"} Horse Info`}
            </RunnerInfoShowMoreLink>
          </RunnerInfoHeader>
          <RunnerMainInfo data-qa-label={`${props.qaLabel}-main`}>
            <Fragment>
              {mainInfo
                .map((infoKey) => {
                  const runnerInfo = props.runner[infoKey] || "-";
                  return (
                    (infoKey === "age" && `${runnerInfo} year old`) ||
                    (infoKey === "weight" && `${runnerInfo} lbs`) ||
                    runnerInfo
                  );
                })
                .join(", ")}
              {renderRunnerFlags()}
            </Fragment>
          </RunnerMainInfo>
          <RunnerComplementaryInfo
            isVisible={showMoreInfo}
            data-qa-label={`${props.qaLabel}-complementary`}
          >
            {complementaryInfo.map((infoKey) => {
              const hasChanges = complementaryInfoKeyChanges.includes(infoKey);
              return (
                <ComplementaryInfo
                  key={infoKey.replace(/\s/g, "")}
                  data-qa-label={`${props.qaLabel}-complementary-${infoKey}`}
                >
                  <ComplementaryInfoLabel
                    data-qa-label={`${props.qaLabel}-complementary-${infoKey}-label`}
                    hasChanges={hasChanges}
                  >
                    {upperFirst(infoKey.replace("ownerName", "owner"))}
                  </ComplementaryInfoLabel>
                  {hasChanges && (
                    <IconContainer>
                      <Icon
                        icon={changes}
                        size={15}
                        color={buildColor("orange", "400")}
                        className="icon-transformation"
                      />
                    </IconContainer>
                  )}
                  <ComplementaryInfoValue
                    data-qa-label={`${props.qaLabel}-complementary-${infoKey}-value`}
                    hasChanges={hasChanges}
                  >
                    {props.runner[infoKey] || "-"}
                  </ComplementaryInfoValue>
                </ComplementaryInfo>
              );
            })}
            {props.children && (
              <RunnerComplementaryInfo smallPadding isVisible>
                {props.children}
              </RunnerComplementaryInfo>
            )}
          </RunnerComplementaryInfo>
        </RunnerInfoContainer>
      ),
    [JSON.stringify(props.runner), showMoreInfo]
  );
};

RunnerInfo.defaultProps = {
  title: "",
  raceTypeCode: "T",
  children: null,
  qaLabel: "runner-info",
  handicappingRunnerFlags: {
    title: "",
    subtitle: "",
    subtitleRunnerFlags: "",
    flags: []
  }
};

export default RunnerInfo;
